import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/Login.css';
import api from '../../utils/api';
import { REFERENCE_DATA_AUTH_EVENT } from '../../contexts/ReferenceDataContext';
import {
  SESSION_EXPIRED_PARAM,
  clearAuthStorage,
  clearSessionScopedState,
  isTokenExpired,
} from '../../utils/auth';

const getDashboardPath = (role) => {
  const key = String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');
  return key === 'admin' ? '/admin/dashboard' : '/employee/dashboard';
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('Test@123');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Tell the user why they are back here when the session ran out rather than silently showing the form.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(SESSION_EXPIRED_PARAM) === '1') {
      setError('Your session has expired. Please sign in again.');
    }
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    const userStr = sessionStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.token && isTokenExpired(user.token)) {
          // Expired token: drop it instead of bouncing back into an app that cannot load anything.
          clearAuthStorage();
          return;
        }
        if (user.token && user.isAuthenticated) {
          // User is already logged in, always route to dashboard by role
          navigate(getDashboardPath(user.role));
        }
      } catch (error) {
        // Invalid user data, clear it
        sessionStorage.removeItem('user');
      }
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Call backend API for authentication
      const response = await api.login(email, password);
      
      if (response.success && response.token) {
        const responseUser = response.data || {};

        // Preserve permission payload from backend so UI checks stay in sync
        // with Role & Permission settings configured by admins.
        const permissionPayload =
          responseUser.permissions ||
          responseUser.permissionFeatures ||
          responseUser.allowedPermissions ||
          responseUser.features ||
          [];

        const overridePayload =
          responseUser.permissionOverrides && typeof responseUser.permissionOverrides === 'object' && !Array.isArray(responseUser.permissionOverrides)
            ? responseUser.permissionOverrides
            : {};

        // Store user data with JWT token
        const userData = {
          token: response.token,
          id: responseUser.id,
          name: responseUser.name,
          email: responseUser.email,
          role: responseUser.role,
          jobType: responseUser.jobType ?? '',
          jobTitle: responseUser.jobTitle ?? responseUser.job_title ?? '',
          permissions: permissionPayload,
          permissionOverrides: overridePayload,
          isAuthenticated: true
        };
        
        // Safety net for sessions that ended without a Logout click (tab left open, expired token):
        // drop any list filters/tabs the previous user left behind before the new session starts.
        clearSessionScopedState();
        sessionStorage.setItem('user', JSON.stringify(userData));
        window.dispatchEvent(new Event(REFERENCE_DATA_AUTH_EVENT));

        // Always route to dashboard after successful login
        navigate(getDashboardPath(responseUser.role));
      } else {
        setError(response.message || 'Authentication failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">AM</div>
          <h1 className="login-title">Asset Management</h1>
          <p className="login-subtitle">Sign in to your account</p>
        </div>

        {error && (
          <div className="login-error">
            {error}
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              disabled={loading}
            />
          </div>

          <div className="login-form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="login-loading"></span>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Don't have an account? Contact your administrator to create one.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;

